import { motion } from 'framer-motion'
import { useEffect, useRef, useState } from 'react'
import { IoSearchOutline } from 'react-icons/io5'
import { useStreetsSearch } from '../../../api/hooks/use.streets'
import BreadcrumbsComponent from '../../../components/ui/breadcrumbs.component'
import useWindowWidth from '../../../utils/use.window.width'
import * as styled from './styled'

export interface Street {
	id: number
	fullStreetName: string
	streetName: string
}

interface HeadlineProps {
	onSearch: (street: number | undefined) => void
}

export const HeadlineComponent: React.FC<HeadlineProps> = ({ onSearch }) => {
	const windowWidth = useWindowWidth()
	const isMobile = windowWidth <= 530
	const isTinyMobile = windowWidth <= 425

	const [value, setValue] = useState('')
	const [debouncedValue, setDebouncedValue] = useState('')
	const [isListOpen, setIsListOpen] = useState(false)
	const [isOverlayOpen, setIsOverlayOpen] = useState(false)

	const [isSearchVisible, setIsSearchVisible] = useState(false)
	const [lastValidValue, setLastValidValue] = useState('')

	const searchTextRef = useRef<HTMLDivElement>(null)
	const [overlayTop, setOverlayTop] = useState(200)

	const openOverlay = () => {
		if (searchTextRef.current) {
			const rect = searchTextRef.current.getBoundingClientRect()
			setOverlayTop(rect.top + 30)
		}
		setIsOverlayOpen(true)
	}

	useEffect(() => {
		const handler = setTimeout(() => setDebouncedValue(value), 400)
		return () => clearTimeout(handler)
	}, [value])

	const { data: streets } = useStreetsSearch(debouncedValue)

	const handleSelectStreet = (street: Street) => {
		setIsListOpen(false)
		setValue(street.fullStreetName)
		setLastValidValue(street.fullStreetName)
		setIsOverlayOpen(false)
		onSearch(street.id)
	}

	const handleSearchClick = () => {
    const selectedStreet = streets?.find(s => s.fullStreetName === value);
    
    if (selectedStreet) {
        onSearch(selectedStreet.id);
    } else if (value === '') {
        onSearch(undefined);
    }
    
    setIsListOpen(false);
    setIsOverlayOpen(false);
    if (isTinyMobile) setIsSearchVisible(false);
};

	const handleBlur = () => {
		setTimeout(() => {
			if (isListOpen) return

			if (value === lastValidValue) {
				return
			}

			const exists = streets?.some(
				s => s.fullStreetName.toLowerCase() === value.toLowerCase(),
			)

			if (!exists && value !== '') {
				setValue(lastValidValue)
			}
		}, 300)
	}

	const clearInput = () => {
		setValue('')
		onSearch(undefined)
		setIsListOpen(false)
	}

	const closeOverlay = () => {
		setIsOverlayOpen(false)
		setIsListOpen(false)
	}

	const handleToggleSearch = () => {
		if (isTinyMobile) {
			openOverlay()
		} else {
			setIsSearchVisible(!isSearchVisible)
		}
	}

	const dropdownVariants = {
		hidden: { opacity: 0, y: -15 },
		visible: { opacity: 1, y: 0 },
		exit: { opacity: 0, y: -15 },
	}

	const SearchContent = (
		<motion.div
			variants={dropdownVariants}
			initial='hidden'
			animate='visible'
			exit='exit'
			transition={{ duration: 0.3, ease: 'easeOut' }}
			style={{ width: '100%', zIndex: 10 }}
		>
			<styled.SearchWrapper style={{ position: 'relative', margin: 0 }}>
				<styled.Input
					autoFocus={isSearchVisible || isOverlayOpen}
					value={value}
					onChange={e => {
						const newValue = e.target.value
						if (
							value === lastValidValue &&
							value !== '' &&
							newValue !== value
						) {
							const charEntered = (e.nativeEvent as InputEvent).data

							if (charEntered) {
								setValue(charEntered) 
							} else {
								setValue('')
							}
						} else {
							setValue(newValue)
						}

						setIsListOpen(true)
					}}
					onBlur={handleBlur}
					placeholder='Почніть вводити назву вулиці'
				/>
				{isListOpen && streets && streets.length > 0 && (
					<styled.DropdownList>
						{streets.map(street => (
							<styled.DropdownItem
								key={street.id}
								onClick={() => handleSelectStreet(street)}
							>
								{street.fullStreetName}
							</styled.DropdownItem>
						))}
					</styled.DropdownList>
				)}
				{value && (
					<styled.ClearButton onClick={clearInput}>✕</styled.ClearButton>
				)}
				<styled.Button onClick={handleSearchClick}>Показати</styled.Button>
			</styled.SearchWrapper>
		</motion.div>
	)

	return (
		<styled.HeadlineWrapper>
			{!isMobile && (
				<BreadcrumbsComponent
					breadcrumbs={[
						{ title: 'Головна', path: '/' },
						{ title: 'Ремонтні роботи', path: '/repair-work' },
					]}
				/>
			)}

			<styled.HeadlineContainer>
				<styled.HeadlineActionsColumn>
					<styled.HeadlineTitle>
						Ремонтні <br /> роботи
					</styled.HeadlineTitle>
				</styled.HeadlineActionsColumn>

				<styled.HeadlineTextColumn>
					Тут ви можете переглянути інформацію про заплановані, поточні та аварійні ремонтні роботи та здійснити пошук за вулицею
				</styled.HeadlineTextColumn>
			</styled.HeadlineContainer>

			<styled.HeadlineSearchContainer>
				<styled.SearchText onClick={handleToggleSearch} ref={searchTextRef}>
					<IoSearchOutline size={24} color='#01904e' />
					Пошук за адресою
				</styled.SearchText>

				{!isTinyMobile && isSearchVisible && SearchContent}
			</styled.HeadlineSearchContainer>

			<styled.SearchOverlay
				$isVisible={isOverlayOpen}
				onClick={e => {
					if (e.target === e.currentTarget) closeOverlay()
				}}
			>
				<styled.SearchWrapper
					style={{
						position: 'relative',
						margin: 0,
						marginTop: `${overlayTop}px`,
					}}
				>
					<styled.Input
						autoFocus={isSearchVisible || isOverlayOpen}
						value={value}
						onChange={e => {
							const newValue = e.target.value

							
							if (
								value === lastValidValue &&
								value !== '' &&
								newValue !== value
							) {
								
								const charEntered = (e.nativeEvent as InputEvent).data

								if (charEntered) {
									setValue(charEntered) 
								} else {
									setValue('')
								}
							} else {
								setValue(newValue)
							}

							setIsListOpen(true)
						}}
						onBlur={handleBlur}
						//   onFocus={handleFocus}
						placeholder='Почніть вводити назву вулиці'
					/>
					{isListOpen && streets && streets.length > 0 && (
						<styled.DropdownList>
							{streets.map(street => (
								<styled.DropdownItem
									key={street.id}
									onClick={() => handleSelectStreet(street)}
								>
									{street.fullStreetName}
								</styled.DropdownItem>
							))}
						</styled.DropdownList>
					)}

					<styled.ClearButton onClick={clearInput}>✕</styled.ClearButton>

					<styled.Button onClick={handleSearchClick}>Показати</styled.Button>
				</styled.SearchWrapper>
			</styled.SearchOverlay>
		</styled.HeadlineWrapper>
	)
}
